/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jan 15 2025
 **/

/**
 * local 환경에서 브라우저에 동시 그리기를 진행할 수 없기 때문에
 * 키보드로 network player를 emulate 하는 hook
 */

import { useRef } from "react";
import { fromEvent } from "rxjs";

import { useWhiteboardActions } from "../useWhiteboardActions";
import useAutoEvent from "./useAutoEvent";
import { DRAWING_STATE, OBJECT_TYPE, PEN_TYPE } from "@drawing/index";
import { WhiteboardHook } from "./useWhiteboard";
import Presets from "@/presets";
import { IDrawingData } from "@/types/types";

export default function useDebugEvents() {
	const whiteboardRef = useRef<WhiteboardHook>();
	const index = useRef(0);
	const id = useRef("debug");
	const drawingState = useRef(DRAWING_STATE.END);

	const whiteboardActions = useWhiteboardActions();

	/* Debug Value */
	const debugMouseSpeed = 5;
	const objectStyle = {
		penType: PEN_TYPE.PEN,
		strokeColor: Presets.penColors[3],
		fillColor: "transparent",
		thickness: Presets.thicknessRange.default,
		alpha: 1,
		objectType: OBJECT_TYPE.FREE_LINE,
		emojiSourceIndex: 0
	};
	let x = 0;
	let y = 0;

	/* Key Events */
	useAutoEvent(
		[
			{
				event: fromEvent<KeyboardEvent>(document, "keydown"),
				handler: (e: KeyboardEvent) => handleKeyDown(e)
			}
		],
		[whiteboardRef.current]
	);

	const handleKeyDown = (event: KeyboardEvent) => {
		const key = event.key;
		// start
		if (key == "z") {
			if (drawingState.current != DRAWING_STATE.END) return;
			setRandomValues();
			drawingState.current = DRAWING_STATE.START;
			whiteboardActions.network.addNetworkPlayer(id.current);
			whiteboardActions.network.drawingStart({ id: id.current, drawingData: getPlayerData() });
		}
		// draw
		else if (key == "a" || key == "s" || key == "d" || key == "w") {
			if (drawingState.current == DRAWING_STATE.END) return;
			drawingState.current = DRAWING_STATE.DRAWING;
			updatePosition(key);
			whiteboardActions.network.drawingTo({ id: id.current, positions: [{ x, y }] });
		}
		// end
		else if (key == "x") {
			if (drawingState.current == DRAWING_STATE.END) return;
			drawingState.current = DRAWING_STATE.END;
			whiteboardActions.network.drawingEnd({ id: id.current, position: { x, y } });
		}
	};

	/* Initlaize */
	const initialize = (whiteboard: WhiteboardHook) => {
		whiteboardRef.current = whiteboard;
	};

	/* Utils */
	const getPlayerData = (): IDrawingData => {
		return {
			playerInfo: {
				id: id.current,
				name: id.current,
				index: index.current
			},
			positions: [{ x, y }],
			toolState: "freeline",
			canDraw: true,
			objectStyle,
			canvasResolution: whiteboardRef.current?.getCanvasResolution()
		};
	};

	const updatePosition = (key: string) => {
		if (key == "a") x -= debugMouseSpeed;
		if (key == "s") y += debugMouseSpeed;
		if (key == "d") x += debugMouseSpeed;
		if (key == "w") y -= debugMouseSpeed;
	};

	const getRandomRange = (min: number, max: number) => {
		return Math.floor(min + Math.random() * (max - min));
	};

	const setRandomValues = () => {
		index.current = index.current + 1;
		id.current = `debug-${index.current}`;
		x = getRandomRange(200, 600);
		y = getRandomRange(200, 600);
	};

	return { initialize };
}
